import { Link } from "react-router-dom";
import { ArrowRight, Phone, Tag, Sparkles, MessagesSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import ReviewsSection from "@/components/ReviewsSection";
import { usePageMeta } from "@/hooks/usePageMeta";
import HeroScrollReveal from "@/components/HeroScrollReveal";
import VideoPlayer from "@/components/VideoPlayer";
import SolutionsSection from "@/components/SolutionsSection";
import FAQ from "@/components/FAQ";
import EstimateForm from "@/components/EstimateForm";
import { useLanguage } from "@/context/LanguageContext";

// Vraies photos d'installations fournies par le client (avant = sans pellicule).
const proofPairs = [
  { before: "/images/avant-1.webp", after: "/images/apres-1.webp" },
  { before: "/images/avant-2.webp", after: "/images/apres-2.webp" },
];

const workImages = [
  "/images/miroir-1.webp", "/images/miroir-3.webp", "/images/givre-2.webp", "/images/realisation-1.webp", "/images/realisation-2.webp", "/images/hero-facade.webp",
];

const benefitIcons = [Tag, Sparkles, MessagesSquare];

export default function Home() {
  const { t } = useLanguage();
  usePageMeta("home");
  const proof = t("proof");
  const work = t("work");
  const benefits = t("benefitsBar.items");
  const form = t("homeForm");

  return (
    <div className="bg-slate-950">
      {/* 1, HERO plein écran */}
      <HeroScrollReveal />

      {/* 2, BARRE DE 3 BÉNÉFICES */}
      <section data-testid="benefits-bar" className="bg-slate-950 px-6 lg:px-8 pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl grid sm:grid-cols-3 divide-y divide-white/10 sm:divide-y-0 sm:divide-x">
          {benefits.map((b, i) => {
            const Icon = benefitIcons[i] || Tag;
            return (
              <div key={b.title} data-testid={`benefit-${i}`} className="p-7 lg:p-9">
                <div className="w-11 h-11 rounded-xl bg-[#C5A880]/15 text-[#C5A880] grid place-items-center">
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <h2 className="font-display text-lg font-bold text-white tracking-tight mt-5">{b.title}</h2>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{b.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* 3, POUR CHAQUE PROBLÈME, UNE SOLUTION */}
      <SolutionsSection />

      {/* 4, PREUVE VISUELLE : vidéo + avant/après */}
      <section data-testid="proof-section" className="py-16 sm:py-24 lg:py-32 bg-slate-950 text-white border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-10 lg:mb-14">
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-[#C5A880]">{proof.eyebrow}</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight leading-[1.1] mt-3">
              {proof.title}
            </h2>
            <p className="mt-5 text-slate-300 text-lg leading-relaxed">{proof.text}</p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3 max-w-6xl mx-auto items-start">
            <div className="flex justify-center">
              <VideoPlayer testId="proof-video" withJsonLd className="w-full max-w-[300px] shadow-[0_32px_80px_rgba(0,0,0,0.45)]" />
            </div>
            {proofPairs.map((pair, i) => (
              <figure key={i} data-testid={`proof-pair-${i}`} className="grid grid-cols-2 gap-3 lg:gap-4">
                {[pair.before, pair.after].map((src, j) => (
                  <div key={src} className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-[9/16] border border-white/10">
                    <img
                      src={src}
                      alt={proof.alts[i][j]}
                      width={923}
                      height={1641}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                    <span
                      className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider backdrop-blur-md ${
                        j === 0 ? "bg-white/85 text-slate-800" : "bg-slate-950/80 text-[#E2D3BE] border border-[#C5A880]/30"
                      }`}
                    >
                      {j === 0 ? proof.before : proof.after}
                    </span>
                  </div>
                ))}
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 5, AVIS CLIENTS (vrais avis Google, carrousel) */}
      <ReviewsSection limit={3} />

      {/* 6, NOS RÉALISATIONS */}
      <section data-testid="work-section" className="py-16 sm:py-24 lg:py-32 bg-slate-950 text-white border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 lg:mb-14">
            <div className="max-w-2xl">
              <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-[#C5A880]">{work.eyebrow}</div>
              <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight leading-[1.1] mt-3">
                {work.title}
              </h2>
            </div>
            <Link to="/contact" data-testid="work-cta" className="flex-shrink-0">
              <Button className="rounded-full bg-blue-600 hover:bg-blue-700 text-white h-12 px-6 font-medium group">
                {t("hero.cta")}
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 lg:gap-4">
            {workImages.map((src, i) => (
              <figure
                key={src}
                data-testid={`work-item-${i}`}
                className="aspect-[4/3] overflow-hidden rounded-2xl bg-slate-900 border border-white/10 group"
              >
                <img
                  src={src}
                  alt={work.alts[i]}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 7, FAQ (5 questions, image à droite) */}
      <FAQ dark image={{ src: "/images/vue-exterieure.webp", alt: "Fenêtre avec pellicule vue de l'extérieur, reflets et verdure" }} />

      {/* 8, FORMULAIRE (section claire contrastante) */}
      <section data-testid="final-cta" className="py-16 sm:py-24 lg:py-32 bg-[#F5F3EE]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">{form.eyebrow}</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight leading-[1.1] text-slate-900 mt-3">
              {form.title}
            </h2>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed">{form.text}</p>
            <a
              href="tel:+14382556438"
              data-testid="final-cta-phone"
              className="mt-8 inline-flex items-center gap-3 text-slate-900 font-semibold hover:text-blue-600 transition-colors"
            >
              <span className="w-11 h-11 rounded-full bg-slate-950 text-[#C5A880] grid place-items-center">
                <Phone className="w-5 h-5" />
              </span>
              438 255 6438
            </a>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-xl shadow-slate-900/5">
            <EstimateForm submitClassName="bg-slate-950 hover:bg-slate-800" />
          </div>
        </div>
      </section>
    </div>
  );
}
